import {
  AdditionalAspectRatio,
  AspectRatio,
  VideoDimensionsHeight,
  VideoDimensionsWidth,
} from '@src/types.ts/video';

import type {
  AspectConfiguration,
  CompositionTemplate,
} from '../CompositionSettings/types';
import type { ExtendedAspectRatio } from '@src/types.ts/video';

export class CompositionNormalizer {
  public static adjustCompositionSourceByAspectConfiguration(
    composition: CompositionTemplate,
    currentVideoAspect: AspectRatio | ExtendedAspectRatio,
  ): CompositionTemplate {
    const source = JSON.parse(JSON.stringify(composition.source));
    const currentVideoDimensions =
      this.getCurrentVideoDimensions(currentVideoAspect);
    const currentAspectConfiguration = this.getCurrentAspectConfiguration(
      composition,
      currentVideoAspect,
    );

    const scaleX = currentAspectConfiguration.scaleX?.toString() ?? '100%';
    const scaleY = currentAspectConfiguration.scaleY?.toString() ?? '100%';
    const positionX = currentAspectConfiguration.positionX.toString();
    const positionY = currentAspectConfiguration.positionY.toString();
    const width = (source.width as string | number)?.toString() ?? '100%';
    const height = (source.height as string | number)?.toString() ?? '100%';

    // Scale is ignored for 'position' (the canvas size is constant), so which
    // axis is passed here makes no difference — they're paired for readability.
    const adjustedPositionX = this.convertToPercentage(
      positionX,
      currentVideoDimensions.width,
      scaleX,
      'position',
    );
    const adjustedPositionY = this.convertToPercentage(
      positionY,
      currentVideoDimensions.height,
      scaleY,
      'position',
    );

    const adjustedWidth = this.convertToPercentage(
      composition.isFullscreen ? '100%' : width,
      currentVideoDimensions.width,
      scaleX,
      'size',
    );
    const adjustedHeight = this.convertToPercentage(
      composition.isFullscreen ? '100%' : height,
      currentVideoDimensions.height,
      scaleY,
      'size',
    );

    return {
      ...composition,
      source: {
        ...source,
        x: adjustedPositionX,
        y: adjustedPositionY,
        width: adjustedWidth,
        height: adjustedHeight,
      },
    };
  }

  static convertToPercentage(
    compositionValue: string,
    currentVideoDimensionValue: number,
    scale: string = '100%',
    type: 'position' | 'size',
  ): string {
    const isCompositionValuePercentage = compositionValue.endsWith('%');

    // Scale is only applied to size values, not position values
    // Position coordinates don't need scaling because the canvas size remains constant
    const scaleMultiplier = type === 'size' ? this.parseScaleValue(scale) : 1;

    // If already a percentage, apply scale multiplier directly
    if (isCompositionValuePercentage) {
      const percentValue = parseFloat(compositionValue);
      return `${percentValue * scaleMultiplier}%`;
    }

    // Convert absolute pixel value to percentage of the video dimension
    const pixelValue = parseFloat(compositionValue);
    const percentageValue =
      (pixelValue / currentVideoDimensionValue) * 100 * scaleMultiplier;

    return `${percentageValue}%`;
  }

  private static getCurrentVideoDimensions(aspectRatio: ExtendedAspectRatio): {
    width: number;
    height: number;
  } {
    switch (aspectRatio) {
      case AspectRatio.AR_16_9:
        return {
          width: VideoDimensionsWidth.AR_16_9,
          height: VideoDimensionsHeight.AR_16_9,
        };
      case AspectRatio.AR_1_1:
        return {
          width: VideoDimensionsWidth.AR_1_1,
          height: VideoDimensionsHeight.AR_1_1,
        };
      case AspectRatio.AR_9_16:
        return {
          width: VideoDimensionsWidth.AR_9_16,
          height: VideoDimensionsHeight.AR_9_16,
        };
      case AdditionalAspectRatio.AR_4_5:
        return {
          width: VideoDimensionsWidth.AR_4_5,
          height: VideoDimensionsHeight.AR_4_5,
        };
      default:
        return {
          width: VideoDimensionsWidth.AR_16_9,
          height: VideoDimensionsHeight.AR_16_9,
        };
    }
  }

  /**
   * Inverse of this class's size math: recovers per-axis scales from an
   * element's live on-canvas size, relative to the composition's authored
   * `source` dimensions.
   *
   * Absolute by construction — it reads no previously stored scale, so a
   * capture can neither drift nor compound the way a baseline-relative
   * computation can. Returns `undefined` when the scales would be meaningless:
   * a fullscreen composition (whose size is forced to 100%, so any ratio is
   * noise) or unusable live/source values.
   */
  static computeAxisScalesFromLiveSize(
    composition: CompositionTemplate,
    currentVideoAspect: AspectRatio | ExtendedAspectRatio,
    liveWidth: unknown,
    liveHeight: unknown,
  ): { scaleX: number; scaleY: number } | undefined {
    if (composition.isFullscreen) return undefined;

    const currentVideoDimensions =
      this.getCurrentVideoDimensions(currentVideoAspect);
    const sourceWidth = (
      composition.source?.width as string | number
    )?.toString();
    const sourceHeight = (
      composition.source?.height as string | number
    )?.toString();
    if (!sourceWidth || !sourceHeight) return undefined;

    // The size the composition renders at scale 1, in canvas percent. Reusing
    // convertToPercentage keeps px and "%" source values handled identically to
    // the forward path.
    const baseWidth = parseFloat(
      this.convertToPercentage(
        sourceWidth,
        currentVideoDimensions.width,
        '100%',
        'size',
      ),
    );
    const baseHeight = parseFloat(
      this.convertToPercentage(
        sourceHeight,
        currentVideoDimensions.height,
        '100%',
        'size',
      ),
    );
    // Live values are canvas percentages; a bare number is treated as a percent
    // magnitude, matching how the renderer echoes x/y back after a stage drag.
    const currentWidth = parseFloat(String(liveWidth ?? ''));
    const currentHeight = parseFloat(String(liveHeight ?? ''));

    if (
      !Number.isFinite(baseWidth) ||
      baseWidth === 0 ||
      !Number.isFinite(baseHeight) ||
      baseHeight === 0 ||
      !Number.isFinite(currentWidth) ||
      !Number.isFinite(currentHeight)
    ) {
      return undefined;
    }

    return {
      scaleX: this.roundScale(currentWidth / baseWidth),
      scaleY: this.roundScale(currentHeight / baseHeight),
    };
  }

  /** Per-aspect scales are stored at 5dp — enough to round-trip a pixel. */
  private static roundScale(value: number): number {
    return Math.round(value * 100000) / 100000;
  }

  /**
   * Per-axis scale values for a config, falling back to the legacy uniform
   * `scale` when an axis is absent (see AspectConfiguration).
   */
  private static axisScaleValues(
    config: AspectConfiguration | null | undefined,
    defaultScale: string | number,
  ): { scaleX: string | number; scaleY: string | number } {
    return {
      scaleX: config?.scaleX ?? config?.scale ?? defaultScale,
      scaleY: config?.scaleY ?? config?.scale ?? defaultScale,
    };
  }

  /**
   * Resolves a config's per-axis scales to numeric multipliers. Use this rather
   * than reading `scale` directly, so pre-per-axis records keep working.
   */
  static resolveAxisScales(config: AspectConfiguration | null | undefined): {
    scaleX: number;
    scaleY: number;
  } {
    const { scaleX, scaleY } = this.axisScaleValues(config, 1);
    return {
      scaleX: this.parseScaleValue(scaleX),
      scaleY: this.parseScaleValue(scaleY),
    };
  }

  private static getCurrentAspectConfiguration(
    composition: CompositionTemplate,
    aspectRatio: ExtendedAspectRatio,
  ): {
    positionX: string;
    positionY: string;
    // The scales may be numeric multipliers (per-aspect AspectConfiguration
    // scales are `string | number | null`); the sole caller `.toString()`s them.
    scaleX: string | number;
    scaleY: string | number;
  } {
    const defaultPositionX = '50%';
    const defaultPositionY = '50%';
    const defaultScale = '100%';
    switch (aspectRatio) {
      case AspectRatio.AR_16_9:
        return {
          positionX: composition?.sixteenByNine?.positionX ?? defaultPositionX,
          positionY: composition?.sixteenByNine?.positionY ?? defaultPositionY,
          ...this.axisScaleValues(composition?.sixteenByNine, defaultScale),
        };
      case AspectRatio.AR_1_1:
        return {
          positionX: composition?.oneByOne?.positionX ?? defaultPositionX,
          positionY: composition?.oneByOne?.positionY ?? defaultPositionY,
          ...this.axisScaleValues(composition?.oneByOne, defaultScale),
        };
      case AspectRatio.AR_9_16:
        return {
          positionX: composition?.nineBySixteen?.positionX ?? defaultPositionX,
          positionY: composition?.nineBySixteen?.positionY ?? defaultPositionY,
          ...this.axisScaleValues(composition?.nineBySixteen, defaultScale),
        };
      default:
        return {
          positionX: defaultPositionX,
          positionY: defaultPositionY,
          ...this.axisScaleValues(null, defaultScale),
        };
    }
  }

  /**
   * Extracts aspect configuration from element metadata for a given aspect ratio
   */
  static getAspectConfigurationFromMetadata(
    metadata: {
      sixteenByNine?: AspectConfiguration;
      nineBySixteen?: AspectConfiguration;
      oneByOne?: AspectConfiguration;
    },
    aspectRatio: AspectRatio,
  ): AspectConfiguration {
    const defaultConfiguration: AspectConfiguration = {
      positionX: '50%',
      positionY: '50%',
      scale: '100%',
    };
    switch (aspectRatio) {
      case AspectRatio.AR_16_9:
        return metadata?.sixteenByNine ?? defaultConfiguration;
      case AspectRatio.AR_9_16:
        return metadata?.nineBySixteen ?? defaultConfiguration;
      case AspectRatio.AR_1_1:
        return metadata?.oneByOne ?? defaultConfiguration;
      default:
        return defaultConfiguration;
    }
  }

  /**
   * Parses a scale value ("120%" | "1.2" | number) to a numeric factor.
   * Returns 1 for nullish or unparseable (NaN) input.
   */
  static parseScaleValue(scale?: string | number | null): number {
    if (scale == null) return 1;
    const s = String(scale);
    const n = s.endsWith('%') ? parseFloat(s) / 100 : parseFloat(s);
    return Number.isFinite(n) ? n : 1;
  }

  /**
   * Converts a percentage string to pixel value given a reference dimension
   */
  static convertPercentageToPixels(
    value: string | number | null | undefined,
    referenceDimension: number,
  ): number {
    if (!value) return 0;
    const valueStr = value.toString();
    if (valueStr.endsWith('%')) {
      return (parseFloat(valueStr) * referenceDimension) / 100;
    }
    return parseFloat(valueStr);
  }

  /**
   * Adjusts a dimension value by scaling it from old scale to new scale
   */
  static adjustDimensionByScale(
    originalValue: number,
    oldScale: number,
    newScale: number,
  ): number {
    return (originalValue / oldScale) * newScale;
  }
}
